"use client";

import { useEffect, useRef, useState } from "react";
import { IconCloudUpload, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

export const ACCEPTED_PROOF = ".pdf,.jpg,.jpeg,.png,.webp,.heic";

/**
 * What the clipboard calls an image. Chrome hands every pasted image over as
 * `image.png` — a screenshot of the banking app, a WhatsApp photo of the slip —
 * so a name in this set is stamped with the moment instead, the way the
 * patient's Media & Files folder does it. A file dragged in from the desktop
 * keeps its real name.
 */
const GENERIC_NAMES = new Set(["", "image", "screenshot", "unknown", "untitled"]);

const EXTENSION_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "application/pdf": "pdf",
};

function isProof(file: File): boolean {
  return /^image\//.test(file.type) || file.type === "application/pdf" || /\.(pdf|jpe?g|png|webp|heic)$/i.test(file.name);
}

/** `image.png` pasted at 2026-09-30 10:14:05 → `bank-confirmation-20260930-101405.png`. */
function named(file: File, prefix: string): File {
  const bare = file.name.replace(/\.[^.]+$/, "").trim();
  if (!GENERIC_NAMES.has(bare.toLowerCase())) return file;

  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const extension = EXTENSION_BY_TYPE[file.type] ?? (file.name.includes(".") ? file.name.split(".").pop() : "png");

  return new File([file], `${prefix}-${stamp}.${extension}`, { type: file.type });
}

interface ProofDropFieldProps {
  value: File | null;
  onChange: (file: File | null) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** Listen for Ctrl+V on the page while mounted — off when another paste target is up. */
  paste?: boolean;
  /** The empty box's invitation; defaults to the bank-confirmation wording. */
  dropText?: string;
  /** What a nameless pasted image is called; defaults to `bank-confirmation`. */
  namePrefix?: string;
}

/**
 * ArtDent 260930 — the bank's confirmation, taken the way the patient's
 * Media & Files folder takes a document: dropped, pasted straight from the
 * clipboard, or picked. Holds ONE file in form state; the host dialog uploads
 * it once its own save has gone through, so a failed save leaves nothing
 * filed against a payment that never changed.
 */
export function ProofDropField({ value, onChange, label, hint, disabled, paste = true, dropText, namePrefix = "bank-confirmation" }: ProofDropFieldProps) {
  const t = useTranslations("payments");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = (file: File | undefined | null) => {
    if (disabled || !file) return;
    if (!isProof(file)) {
      setError(t("proofDrop.wrongType"));
      return;
    }
    setError(null);
    onChange(named(file, namePrefix));
  };

  // The paste lands on the document: the person's last act was a copy in the
  // banking app or WhatsApp, and asking them to click the box first would be
  // a step whose only purpose is the event model. A paste aimed at a text
  // field is left alone.
  useEffect(() => {
    if (!paste || disabled || value) return;
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;
      const file = event.clipboardData?.files?.[0];
      if (!file) return;
      event.preventDefault();
      accept(file);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paste, disabled, value]);

  return (
    <div className="space-y-1.5">
      <div>
        <p className="text-[12.5px] font-medium text-foreground">{label}</p>
        {hint && <p className="text-[11.5px] text-text-dim">{hint}</p>}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_PROOF}
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          accept(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      {value ? (
        <div className="grid h-[46px] grid-cols-[42px_1fr_auto_26px] items-center gap-2 rounded-[10px] border border-border bg-surface-2 pr-2">
          <span
            className={cn(
              "grid h-full place-items-center rounded-l-[9px] border-r border-border bg-surface-3 font-mono text-[9px] font-medium tracking-[0.06em]",
              /\.pdf$/i.test(value.name) ? "text-red-600 dark:text-red-400" : "text-accent-soft",
            )}
          >
            {/\.pdf$/i.test(value.name) ? "PDF" : "IMG"}
          </span>
          <span className="truncate text-[12.5px]" title={value.name}>{value.name}</span>
          <span className="font-mono text-[11px] text-text-dim">{(value.size / 1024 / 1024).toFixed(1)} MB</span>
          <button
            type="button"
            aria-label={t("proofDrop.remove")}
            disabled={disabled}
            onClick={() => {
              setError(null);
              onChange(null);
            }}
            className="grid size-[26px] place-items-center rounded-md text-text-dim transition-colors hover:bg-surface-3 hover:text-foreground"
          >
            <IconX className="size-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            accept(event.dataTransfer.files?.[0]);
          }}
          className={cn(
            "flex h-[88px] w-full flex-col items-center justify-center gap-1 rounded-[10px] border border-dashed border-border-strong text-[12.5px] text-text-dim transition-colors hover:border-accent hover:bg-accent-tint-2 hover:text-muted-foreground",
            dragging && "border-accent bg-accent-tint-2 text-muted-foreground",
            error && "border-destructive",
          )}
        >
          <IconCloudUpload className="size-5 text-accent-soft" />
          <span className="font-medium text-foreground">{dropText ?? t("proofDrop.drop")}</span>
          <span className="font-mono text-[10.5px]">{t("proofDrop.constraints")}</span>
        </button>
      )}

      {error && <p className="text-[11.5px] text-destructive">{error}</p>}
    </div>
  );
}
